import type {
  ApiDashboardElement,
  DataSourceCandidatesResponse,
  DimensionSource,
  SelectionSchemaMeasurePoint,
  Source,
  WidgetDimensionsResponse,
} from '@highimpact-frontend/api-types';
import { isWidgetDimensionsResponse } from '@highimpact-frontend/api-types';
import {
  dimensionSourceToSource,
  getDimensionSourceKey,
} from '@highimpact/source-selector';

import {
  getSourceSelectionKey,
  resolveSourcesFromCatalog,
  syncAxisSourceSelectionFromCatalogResponse,
  type AxisSourceSelection,
} from './resolveSourcesFromCatalog';

// -- helper functions --

/**
 * Builds placeholder sources from a widget element's saved selection paths.
 */
export function placeholderSourcesFromElement(
  element: ApiDashboardElement
): Source[] {
  const paths = (element.data_source as SelectionSchemaMeasurePoint[]) ?? [];

  return paths.map((path) => ({
    measure_point_id: path.measure_point_id,
    measure_type_id: path.measure_type_id,
    measurement_unit: '-',
    physical_quantity: '-',
    label: `${path.measure_point_id}-${path.measure_type_id}`,
  }));
}

/**
 * Maps catalog-backed dimension sources back to legacy axis source lists.
 */
export function dimensionSelectionToAxisSources(
  selection: Readonly<Record<string, readonly DimensionSource[]>>
): AxisSourceSelection {
  return Object.fromEntries(
    Object.entries(selection).map(([dimensionKey, dimensionSources]) => [
      dimensionKey,
      dimensionSources.map((dimensionSource) =>
        dimensionSourceToSource(dimensionSource)
      ),
    ])
  );
}

/**
 * Finds matching dimension catalog rows for the currently selected sources.
 */
export function matchDimensionSourcesFromCatalog(
  catalog: WidgetDimensionsResponse,
  dimensionKey: string,
  sources: readonly Source[]
): DimensionSource[] {
  const catalogOptions = catalog.independent_dimensions?.[dimensionKey] ?? [];
  const catalogByKey = new Map(
    catalogOptions.map((dimensionSource) => [
      getDimensionSourceKey(dimensionSource),
      dimensionSource,
    ])
  );

  return sources
    .map((source) => catalogByKey.get(getSourceSelectionKey(source)))
    .filter((source): source is DimensionSource => Boolean(source));
}

/**
 * Builds an independent-dimension selection map from saved element sources.
 */
export function buildIndependentDimensionSelectionFromElement(
  catalog: WidgetDimensionsResponse | null,
  element: ApiDashboardElement,
  dimensionKeys: readonly string[],
  splitSources: (sources: readonly Source[]) => AxisSourceSelection
): Record<string, DimensionSource[]> {
  const axisSelection = splitSources(placeholderSourcesFromElement(element));

  return Object.fromEntries(
    dimensionKeys.map((dimensionKey) => {
      const axisSources = axisSelection[dimensionKey] ?? [];

      if (!catalog) {
        return [dimensionKey, []];
      }

      return [
        dimensionKey,
        matchDimensionSourcesFromCatalog(catalog, dimensionKey, axisSources),
      ];
    })
  );
}

/**
 * Syncs an independent-dimension selection map from any catalog response shape.
 */
export function syncIndependentDimensionSelectionFromCatalog(
  catalog: DataSourceCandidatesResponse,
  dimensionKeys: readonly string[],
  selection: Readonly<Record<string, readonly DimensionSource[]>>
): Record<string, DimensionSource[]> {
  if (!isWidgetDimensionsResponse(catalog)) {
    return Object.fromEntries(
      dimensionKeys.map((dimensionKey) => [
        dimensionKey,
        [...(selection[dimensionKey] ?? [])],
      ])
    );
  }

  const syncedAxisSelection = syncAxisSourceSelectionFromCatalogResponse(
    catalog,
    dimensionSelectionToAxisSources(selection)
  );

  return Object.fromEntries(
    dimensionKeys.map((dimensionKey) => [
      dimensionKey,
      matchDimensionSourcesFromCatalog(
        catalog,
        dimensionKey,
        syncedAxisSelection[dimensionKey] ?? []
      ),
    ])
  );
}

/**
 * Resolves each independent-dimension slot against the fetched catalog.
 */
export function resolveIndependentDimensionSelectionFromCatalog(
  catalog: DataSourceCandidatesResponse | null,
  selection: Readonly<Record<string, readonly DimensionSource[]>>,
  dimensionKeys: readonly string[]
): Record<string, Source[]> {
  const axisSelection = dimensionSelectionToAxisSources(selection);
  const resolved: Record<string, Source[]> = {};

  dimensionKeys.forEach((dimensionKey) => {
    const axisSources = axisSelection[dimensionKey] ?? [];
    resolved[dimensionKey] = resolveSourcesFromCatalog(
      catalog,
      axisSources.map((source) => ({
        measure_point_id: source.measure_point_id,
        measure_type_id: source.measure_type_id,
      })),
      axisSources
    );
  });

  return resolved;
}
